import PageStoryImage from 'components/PageStoryImage';
import PageIntroBand from 'components/PageIntroBand';
import './CareersPage.css';
import 'components/partners/PartnerApplication.css';
import './CandidateApplication.css';
import { FlowButton } from 'components/ui/flow-button';
import HeroBackground from 'components/HeroBackground';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ChevronLeft, ClipboardCheck, FileText, FolderOpen, LockKeyhole, Loader2, MessageCircle, MonitorPlay, Target, Users, XCircle } from 'lucide-react';
import { useReveal } from 'hooks/useReveal';
import { useSiteContent } from 'hooks/useSiteContent';
import { saveApplicationDraft, extractCarrierFlowReference, submitJobApplication, uploadApplicationCv } from 'lib/sitePublicApi';
import HeroScrollRail from 'components/HeroScrollRail';
import SiteHeroCtas, {
  HERO_CTA_PRIMARY_CLASS,
  HERO_CTA_SECONDARY_CLASS,
  PAGE_HERO_BODY_CLASS,
  PAGE_HERO_CONTAINER_CLASS,
  PAGE_HERO_CTA_ROW_CLASS,
  PAGE_HERO_EYEBROW_CLASS,
  PAGE_HERO_H1_CLASS,
} from 'components/SiteHeroCtas';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'components/ui/accordion';
import { splitSiteContentParagraphs } from 'lib/siteContentCms';
import { toast } from 'sonner';
import { applicationCvError } from 'lib/applicationCv';

const CAREERS_ASSESSMENT_STORAGE_KEY = 'magellanCareersAssessmentDraft';
const CAREERS_HERO_CONTENT = {
  eyebrow: "Work With Us",
  title: "Work from home in Safety and ELD.",
  body: "Learn how US carriers operate and prepare for Safety and electronic logging device (ELD) support roles, from driver paperwork to daily log support.\n\nMagellan International provides learning materials and refers suitable candidates to US carriers. If hired, you work directly for the carrier.",
  primaryCtaLabel: 'Start your application',
  primaryCtaPath: '#application-form',
  secondaryCtaLabel: "Explore the opportunity",
  secondaryCtaPath: '#careers-standard',
};
const CAREERS_HERO_PARAGRAPH_LIMIT = 2;
const CAREERS_HERO_SECTION_CLASS = 'relative flex min-h-[100svh] w-full items-center overflow-hidden md:h-screen md:min-h-[680px]';
const CAREERS_MARQUEE_ITEMS = [
  "Work from home",
  "Learn logistics",
  "Safety opportunities",
  "ELD opportunities",
  "Driver records",
  "Log support",
  "Video learning",
  "Practical training",
  "Career development",
  "US-based carriers"
];

function renderGoldHeroTitle(title) {
  const words = String(title || '').trim().split(/\s+/).filter(Boolean);
  const goldStartIndex = Math.max(0, words.length - 3);

  return words.map((word, index) => (
    <Fragment key={`${word}-${index}`}>
      {index === goldStartIndex && <br />}
      <span aria-hidden="true" className="mr-3 inline-block overflow-hidden align-bottom sm:mr-4">
        <span className={`inline-block ${index >= goldStartIndex ? 'italic shimmer-text' : ''}`}>
          {word}
        </span>
      </span>
    </Fragment>
  ));
}

function CareersIntroBand() {
  return (
    <PageIntroBand
      testId="careers-page-intro"
      label="Your next step"
      title="Bring your experience. Or your willingness to learn."
      backgroundText="WORK WITH US"
      paragraphs={[
        "Start with the basics if you are new to logistics, or build on the industry experience you already have.",
        "Explore the positions below, then see how selection and training work before you apply.",
      ]}
    />
  );
}

const ROLE_AREAS = [
  { title: 'Safety — Open now', copy: "Help manage a carrier’s safety paperwork, including permits, driver qualification (DQ) files, drug and alcohol testing records, and previous-employment verification. Work with drivers and the carrier’s team to obtain the documents needed for daily operations.", icon: FolderOpen },
  { title: 'ELD — Open now', copy: "Review driver logs, help with appropriate corrections and speak with drivers when they need log support. Assist with estimated arrival times (ETAs) and route planning, taking account of available driving hours and the carrier’s operating requirements.", icon: MonitorPlay },
  { title: "Accounting — At full capacity", copy: "Accounting is at full capacity. Applications are currently closed.", icon: ClipboardCheck },
  { title: "HR — At full capacity", copy: "HR is at full capacity. Applications are currently closed.", icon: Users },
  { title: "Track & trace — At full capacity", copy: "Track & trace is at full capacity. Applications are currently closed.", icon: Target },
];

const HERO_SIGNALS = [
  { label: "Work from home", icon: Target },
  { label: "Practical training", icon: CheckCircle2 },
  { label: "New and experienced", icon: LockKeyhole },
  { label: "Direct carrier hiring", icon: MessageCircle },
];

const GOOD_FIT_ITEMS = [
  "You like checking details in records and keeping files organized.",
  "You are comfortable speaking with drivers and following up for missing information.",
  "You want to understand how paperwork or driving hours affect a carrier’s day."
];

const NOT_GOOD_FIT_ITEMS = [
  "You prefer work that does not involve driver records or log questions.",
  "You are looking only for an office-based position.",
  "You cannot make time for the assigned training and tests."
];

const ROLE_WORK_MODES = [
  {
    title: "Free assigned learning",
    copy: "Take the assigned selection modules at no charge. Paid public courses are separate and optional.",
    icon: MessageCircle,
  },
  {
    title: "Preparation for daily tasks",
    copy: "Video lessons connect logistics topics to tasks such as managing driver paperwork and helping drivers with logs.",
    icon: FileText,
  },
  {
    title: "A route to direct carrier employment",
    copy: "Suitable candidates can be referred to a US carrier for consideration. If hired, you work directly for that carrier.",
    icon: ClipboardCheck,
  },
];

const HIRING_PROCESS = [
  [
    "Your application",
    "Share your details, availability and optional CV. Choose your experience level; experienced applicants also select their previous departments and answer the relevant questions."
  ],
  [
    "An English questionnaire",
    "Suitable applicants are invited by email to a written English questionnaire. Questions cover previous industry work for experienced candidates, or general work habits and everyday situations for newcomers."
  ],
  [
    "Two introductory modules",
    "Selected candidates take two introductory modules on logistics and industry positions, with questions after each video and a test after each module."
  ],
  [
    "Meet, then specialize",
    "Complete the introductory requirements, then meet with us. If we both choose to continue, take three position-specific modules with video questions and module tests."
  ],
  [
    "Referral to a US carrier",
    "Candidates who meet the training requirements and suit a position can be referred to a US carrier for a hiring decision."
  ]
];

const BEFORE_APPLY_ITEMS = [
  "Mention permits, driver files, testing records or employment checks you handled, and the systems you used.",
  "Mention your log review, driver support, ETA or route-planning work, and the systems you used.",
  "Include your time zone and available hours so we can consider carrier schedules that fit.",
  "Share your other work, interests and reasons for learning logistics."
];

const CAREERS_FAQS = [
  [
    "Can I apply without logistics experience?",
    "Yes. Choose the newcomer path in your application; your questionnaire does not require previous logistics experience."
  ],
  [
    "Which positions can I apply for?",
    "Safety and ELD. Accounting, HR and Track & trace are at full capacity and cannot be selected."
  ],
  [
    "Will I work from home, and who hires me?",
    "If hired, you work from home directly for the carrier. Magellan provides training and referrals."
  ],
  [
    "What are the working hours and pay?",
    "The carrier discusses these with you. Some roles cover weekdays during US business hours; others include rotating shifts and weekends. Pay and schedules are agreed for the specific position."
  ],
  [
    "Do I have to pay for training?",
    "No. All assigned selection modules are free. Public courses are separate and optional."
  ]
];

const CAREERS_IMAGES = {
  hero: '/images/careers/work-with-us-hero.jpg',
  review: '/images/careers/document-review.jpg',
  desk: '/images/careers/desk-headset.jpg',
  truck: '/images/careers/night-truck.jpg',
};

// Work with us (website launch L6, Ivan 2026-10-15): Safety and ELD are open now; the others are coming soon.
const ROLE_OPTIONS = [
  ['safety', 'Safety'],
  ['eld', 'ELD'],
];
const ROLES_COMING_SOON = "Accounting (At full capacity) · HR (At full capacity) · Track & trace (At full capacity).";

const AVAILABILITY_OPTIONS = [
  ['full-time', 'Full-time'],
  ['part-time', 'Part-time'],
  ['project-based', 'Project-based'],
  ['internship', 'Internship / junior support'],
];

const YES_NO_OPTIONS = [
  ['yes', 'Yes'],
  ['no', 'No'],
  ['limited', 'Limited'],
];

const APPLICATION_STEPS = [
  {
    key: 'basic',
    eyebrow: 'Step 01',
    title: 'Basic information',
    helper: 'Use the email address where we can reach you.',
    required: ['name', 'email', 'location'],
  },
  {
    key: 'role',
    eyebrow: 'Step 02',
    title: 'Role and availability',
    helper: 'Select a role and tell us when you can work.',
    required: ['role', 'availability'],
  },
  {
    key: 'experience',
    eyebrow: 'Step 03',
    title: 'Experience',
    helper: 'Include your responsibilities and the tools you used.',
    required: ['experience'],
  },
  {
    key: 'english',
    eyebrow: 'Step 04',
    title: 'English and tools',
    helper: 'Tell us how you use English and which systems you know.',
    required: ['writtenEnglish', 'spokenEnglish'],
  },
  {
    key: 'answers',
    eyebrow: 'Step 05',
    title: 'Short written answers',
    helper: 'Describe how you approach common work situations.',
    required: ['whyFit', 'bestAt'],
  },
  {
    key: 'review',
    eyebrow: 'Step 06',
    title: 'Review and continue',
    helper: 'Confirm your details before continuing to the assessment.',
    required: [],
  },
];

function writeAssessmentDraft(value) {
  saveApplicationDraft(value, CAREERS_ASSESSMENT_STORAGE_KEY);
}

function sanitizeText(value) {
  return String(value || '').trim();
}

function Field({ label, value, onChange, placeholder, type = 'text', required = false, testId, options }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-copy/40 font-body mb-2 block">
        {label}{required ? <span className="text-accent-ink"> *</span> : null}
      </label>
      {options ? (
        <select
          data-testid={testId}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-canvas border-b border-ink/20 text-ink py-3 px-0 focus:border-gold focus:outline-none transition-colors font-body"
        >
          <option value="">Select one</option>
          {options.map(([optionValue, optionLabel]) => (
            <option key={optionValue} value={optionValue}>{optionLabel}</option>
          ))}
        </select>
      ) : (
        <input
          data-testid={testId}
          type={type}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent border-b border-ink/20 text-ink py-3 px-0 focus:border-gold focus:outline-none transition-colors placeholder:text-ink/20 font-body"
          placeholder={placeholder}
        />
      )}
    </div>
  );
}

function TextArea({ label, value, onChange, placeholder, required = false, rows = 4, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-copy/40 font-body mb-2 block">
        {label}{required ? <span className="text-accent-ink"> *</span> : null}
      </label>
      <textarea
        data-testid={testId}
        rows={rows}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-ink/20 text-ink py-3 px-0 focus:border-gold focus:outline-none transition-colors placeholder:text-ink/20 font-body resize-none"
        placeholder={placeholder}
      />
    </div>
  );
}

function ApplicationReceived() {
  const ref = useReveal();

  return (
    <div ref={ref} className="min-h-screen bg-canvas flex items-center">
      <div className="site-content-shell w-full pt-28 pb-20">
        <div className="border border-gold/20 bg-surface p-10 text-center">
          <CheckCircle2 className="w-12 h-12 text-accent-ink mx-auto mb-6" />
          <h1 className="font-display text-3xl text-ink mb-4">Application received.</h1>
          <p className="font-body text-copy/50 max-w-xl mx-auto mb-8">
            Thank you for applying. Magellan may contact you if your background matches the next stage.
          </p>
          <FlowButton to="/work-with-us" className="inline-flex items-center gap-2 border border-ink/20 text-ink px-8 py-4 text-sm uppercase tracking-wider font-body hover:border-gold  transition-colors">
            Back to Work With Us
          </FlowButton>
        </div>
      </div>
    </div>
  );
}

export default function CareersPage() {
  const ref = useReveal();
  const navigate = useNavigate();
  const location = useLocation();
  const cmsHero = useSiteContent('careersHero');
  // L7: the "Role areas" intro is editable in admin (site content: careersIntro).
  const intro = useSiteContent('careersIntro');
  const introTitle = !intro.title || intro.title === 'Magellan hires and works with people across several support areas.'
    ? 'Start with Safety or ELD.' : intro.title;
  const introBody = !intro.body || intro.body === 'Safety and ELD are open now. Candidates may apply from any country if they can work in English and pass the required selection steps.' || intro.body === 'Some roles are client-facing. Some are documentation-heavy. Some are internal support roles. The common standard is the same: accuracy, follow-up, confidentiality, and clean execution.' || intro.body === 'Choose the path that matches your experience or the work you want to learn.' || intro.body === "Applications are open worldwide. Choose the path that matches your experience or the work you want to learn."
    ? "Applications are open worldwide for Safety and ELD. Choose the position that interests you." : intro.body;
  const hero = CAREERS_HERO_CONTENT;
  const heroTitle = hero.title;
  const heroIntroParagraphs = splitSiteContentParagraphs(hero.body).slice(0, CAREERS_HERO_PARAGRAPH_LIMIT);
  const heroImageUrl = cmsHero.imageUrl;
  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', location: '', timeZone: '', linkedIn: '', portfolio: '',
    role: '', roleInterests: '', availability: '', hoursPerWeek: '', workingHours: '', startDate: '', usBusinessHours: '',
    experience: '', logisticsExperience: '', complianceExperience: '', salesExperience: '', writingExperience: '', adminExperience: '', tools: '',
    languages: '', writtenEnglish: '', spokenEnglish: '', callComfort: '', coldCallComfort: '', sopComfort: '',
    workSample: '', whyFit: '', bestAt: '', messyInfo: '', explainMagellan: '', reliability: '', avoidWork: '', unclearInstructions: '', setup: '',
  });
  // L6: the consent text accepted with the application, and an optional CV (one PDF, up to 5 MB).
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentText, setConsentText] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const cvInputRef = useRef(null);
  const cvError = applicationCvError(cvFile);
  useEffect(() => {
    fetch('/api/site/v1/legal/work-consent', { credentials: 'omit', cache: 'no-store' }).then(r => (r.ok ? r.json() : null)).then(r => setConsentText(r?.data || null)).catch(() => {});
  }, []);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [maxUnlockedStep, setMaxUnlockedStep] = useState(0);

  const activeStep = APPLICATION_STEPS[currentStep];
  const progressPercent = Math.round(((currentStep + 1) / APPLICATION_STEPS.length) * 100);
  const missingRequired = useMemo(
    () => activeStep.required.filter((key) => !sanitizeText(form[key])),
    [activeStep.required, form]
  );
  const canContinue = missingRequired.length === 0;

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const scrollToWizard = () => {
    window.setTimeout(() => {
      document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 20);
  };

  useEffect(() => {
    if (!location.pathname.endsWith('/apply')) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);

    return () => window.clearTimeout(timeoutId);
  }, [location.pathname]);

  if (location.pathname.includes('application-received')) {
    return <ApplicationReceived />;
  }

  const goNext = () => {
    if (!canContinue) {
      toast.error('Complete the required fields before continuing.');
      return;
    }
    setCurrentStep((step) => {
      const nextStep = Math.min(step + 1, APPLICATION_STEPS.length - 1);
      setMaxUnlockedStep((unlocked) => Math.max(unlocked, nextStep));
      return nextStep;
    });
    scrollToWizard();
  };

  const goBack = () => {
    setCurrentStep((step) => Math.max(step - 1, 0));
    scrollToWizard();
  };

  const goToUnlockedStep = (targetStep) => {
    if (targetStep > maxUnlockedStep) {
      toast.error('Complete the current section before moving forward.');
      return;
    }
    setCurrentStep(targetStep);
    scrollToWizard();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (currentStep < APPLICATION_STEPS.length - 1) {
      goNext();
      return;
    }

    if (cvError) { toast.error(cvError); cvInputRef.current?.focus(); return; }
    setIsSubmitting(true);

    try {
      if (!consentAccepted) { toast.error('Accept the consent before submitting.'); setIsSubmitting(false); return; }
      const result = await submitJobApplication({ ...form, roleId: form.role, role: ROLE_OPTIONS.find(([value]) => value === form.role)?.[1] || form.role, consentAccepted: true });
      if (result.success && cvFile) {
        const sent = await uploadApplicationCv(extractCarrierFlowReference(result.data), cvFile);
        if (!sent) toast.error('Your application was received, but the CV could not be uploaded. You can send it by email.');
      }
      if (result.success) {
        writeAssessmentDraft({
          applicantReference: extractCarrierFlowReference(result.data),
          initialResult: result.data,
          initialForm: form,
          createdAt: new Date().toISOString(),
        });
        setSubmitted(true);
        toast.success('Base application received. Continue to the assessment page.');
        navigate('/careers/assessment?application=' + encodeURIComponent(extractCarrierFlowReference(result.data)));
      } else {
        toast.error('Failed to submit application. Please try again.');
        console.error('Submission error:', result.error);
      }
    } catch (error) {
      toast.error('An unexpected error occurred.');
      console.error('Unexpected error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepFields = () => {
    switch (activeStep.key) {
      case 'basic':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Field label="Full Name" required testId="careers-input-name" value={form.name} onChange={(value) => updateForm('name', value)} placeholder="Enter your name" />
            <Field label="Email" required type="email" testId="careers-input-email" value={form.email} onChange={(value) => updateForm('email', value)} placeholder="you@email.com" />
            <Field label="Phone" type="tel" testId="careers-input-phone" value={form.phone} onChange={(value) => updateForm('phone', value)} placeholder="Your phone number" />
            <Field label="City and Country" required testId="careers-input-location" value={form.location} onChange={(value) => updateForm('location', value)} placeholder="City, Country" />
            <Field label="Time Zone" value={form.timeZone} onChange={(value) => updateForm('timeZone', value)} placeholder="Your time zone" />
            <Field label="LinkedIn Profile" value={form.linkedIn} onChange={(value) => updateForm('linkedIn', value)} placeholder="LinkedIn URL" />
            <Field label="Portfolio or Work Sample Link" value={form.portfolio} onChange={(value) => updateForm('portfolio', value)} placeholder="Optional URL" />
          </div>
        );
      case 'role':
        return (
          <div className="space-y-8">
            <p className="font-body text-copy/50 text-sm md:col-span-2">{ROLES_COMING_SOON}</p>
            <Field label="Primary Role Interest" required testId="careers-input-role" value={form.role} onChange={(value) => updateForm('role', value)} options={ROLE_OPTIONS} />
            <TextArea label="Additional Role Interests" value={form.roleInterests} onChange={(value) => updateForm('roleInterests', value)} placeholder="List any secondary role areas that genuinely fit your skills." />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Field label="Availability Type" required value={form.availability} onChange={(value) => updateForm('availability', value)} options={AVAILABILITY_OPTIONS} />
              <Field label="Available Hours Per Week" value={form.hoursPerWeek} onChange={(value) => updateForm('hoursPerWeek', value)} placeholder="Hours per week" />
              <Field label="Preferred Working Hours" value={form.workingHours} onChange={(value) => updateForm('workingHours', value)} placeholder="Preferred schedule" />
              <Field label="Start Date" value={form.startDate} onChange={(value) => updateForm('startDate', value)} placeholder="When can you start?" />
              <Field label="Available for U.S. Business Hours" value={form.usBusinessHours} onChange={(value) => updateForm('usBusinessHours', value)} options={YES_NO_OPTIONS} />
            </div>
          </div>
        );
      case 'experience':
        return (
          <div className="space-y-8">
            <TextArea label="Professional Experience" required testId="careers-input-experience" value={form.experience} onChange={(value) => updateForm('experience', value)} placeholder="What companies did you work for, what did you actually do, and for how long?" />
            <TextArea label="Trucking / Logistics Experience" value={form.logisticsExperience} onChange={(value) => updateForm('logisticsExperience', value)} placeholder="Dispatch, brokerage, fleet, safety, recruiting, admin, TMS, or none." />
            <TextArea label="Compliance Experience" value={form.complianceExperience} onChange={(value) => updateForm('complianceExperience', value)} placeholder="Driver files, safety docs, checklists, renewals, audits, documentation, or none." />
            <TextArea label="Sales or Cold Calling Experience" value={form.salesExperience} onChange={(value) => updateForm('salesExperience', value)} placeholder="Calls, appointment setting, scripts, CRM, objection handling, or none." />
            <TextArea label="Writing / SOP Experience" value={form.writingExperience} onChange={(value) => updateForm('writingExperience', value)} placeholder="Emails, SOPs, checklists, blog posts, reports, training material, or none." />
            <TextArea label="Administrative / Customer Support Experience" value={form.adminExperience} onChange={(value) => updateForm('adminExperience', value)} placeholder="Client support, data entry, documents, scheduling, inbox, spreadsheets, or none." />
          </div>
        );
      case 'english':
        return (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Field label="Written English Level" required value={form.writtenEnglish} onChange={(value) => updateForm('writtenEnglish', value)} placeholder="Beginner, intermediate, advanced, fluent" />
              <Field label="Spoken English Level" required value={form.spokenEnglish} onChange={(value) => updateForm('spokenEnglish', value)} placeholder="Beginner, intermediate, advanced, fluent" />
              <Field label="Comfort with Business Calls" value={form.callComfort} onChange={(value) => updateForm('callComfort', value)} options={YES_NO_OPTIONS} />
              <Field label="Comfort with Cold Calls" value={form.coldCallComfort} onChange={(value) => updateForm('coldCallComfort', value)} options={YES_NO_OPTIONS} />
              <Field label="Comfort Writing SOPs or Instructions" value={form.sopComfort} onChange={(value) => updateForm('sopComfort', value)} options={YES_NO_OPTIONS} />
            </div>
            <TextArea label="Tools You Use" value={form.tools} onChange={(value) => updateForm('tools', value)} placeholder="Google Docs, Sheets, Word, Excel, PDF tools, CRM, Canva, Notion, TMS, email tools, AI tools." />
            <Field label="Languages" testId="careers-input-languages" value={form.languages} onChange={(value) => updateForm('languages', value)} placeholder="Languages you use professionally" />
            <Field label="Work Setup" testId="careers-input-setup" value={form.setup} onChange={(value) => updateForm('setup', value)} placeholder="Laptop, headset, internet stability" />
          </div>
        );
      case 'answers':
        return (
          <div className="space-y-8">
            <TextArea label="Why do you want to work with Magellan?" required testId="careers-input-whyfit" value={form.whyFit} onChange={(value) => updateForm('whyFit', value)} placeholder="Explain why you think you fit the work." />
            <TextArea label="What type of work are you best at?" required value={form.bestAt} onChange={(value) => updateForm('bestAt', value)} placeholder="Be specific." />
            <TextArea label="Describe a time you organized messy information." value={form.messyInfo} onChange={(value) => updateForm('messyInfo', value)} placeholder="Describe the situation and what you did." />
            <TextArea label="Explain Magellan to a trucking company owner in two sentences." value={form.explainMagellan} onChange={(value) => updateForm('explainMagellan', value)} placeholder="Write the two sentences." />
            <TextArea label="What makes you reliable?" value={form.reliability} onChange={(value) => updateForm('reliability', value)} placeholder="Give practical evidence, not slogans." />
            <TextArea label="What kind of work do you not want to do?" value={form.avoidWork} onChange={(value) => updateForm('avoidWork', value)} placeholder="Be honest." />
            <TextArea label="How do you handle unclear instructions?" value={form.unclearInstructions} onChange={(value) => updateForm('unclearInstructions', value)} placeholder="Explain how you ask questions and prevent mistakes." />
            <Field label="Optional Work Sample Link" value={form.workSample} onChange={(value) => updateForm('workSample', value)} placeholder="Sales script, SOP, checklist, spreadsheet, blog draft, etc." />
          </div>
        );
      default:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              ['Name', form.name],
              ['Email', form.email],
              ['Location', form.location],
              ['Role', ROLE_OPTIONS.find(([value]) => value === form.role)?.[1] || form.role],
              ['Availability', AVAILABILITY_OPTIONS.find(([value]) => value === form.availability)?.[1] || form.availability],
              ['Written English', form.writtenEnglish],
              ['Spoken English', form.spokenEnglish],
              ['Best work type', form.bestAt],
            ].map(([label, value]) => (
              <div key={label} className="border border-ink/8 bg-canvas p-5">
                <p className="text-[10px] uppercase tracking-[0.25em] text-copy/30 font-body mb-2">{label}</p>
                <p className="font-body text-copy/65 text-sm leading-relaxed">{value || 'Not provided'}</p>
              </div>
            ))}
            <div className="md:col-span-2 border border-ink/8 bg-canvas p-5">
              <label className="font-body text-copy/65 text-sm block mb-3">CV (optional, one PDF up to 5 MB)
                <input ref={cvInputRef} type="file" accept="application/pdf,.pdf" aria-invalid={!!cvError} aria-describedby={cvError ? 'candidate-cv-error' : undefined} className="block mt-2 w-full min-w-0 text-sm" onChange={(e) => setCvFile(e.target.files?.[0] || null)} />
              </label>
              {cvError && <p id="candidate-cv-error" role="alert" className="partner-wizard-error">{cvError}</p>}
              {cvFile && <button type="button" className="font-body text-sm underline mb-3" onClick={() => { setCvFile(null); if (cvInputRef.current) cvInputRef.current.value = ''; }}>Remove CV</button>}
              {consentText && <p className="font-body text-copy/58 text-sm leading-relaxed mb-3 whitespace-pre-line">{consentText.body}</p>}
              <label className="font-body text-copy/65 text-sm flex gap-2"><input type="checkbox" checked={consentAccepted} onChange={(e) => setConsentAccepted(e.target.checked)} />
                <span>I accept the consent{consentText ? '' : ' (shown here once published)'}.</span></label>
            </div>
            <div className="md:col-span-2 border border-gold/20 bg-gold/[0.04] p-6">
              <p className="font-body text-copy/58 text-sm leading-relaxed">
                After submitting this base application, the next screen is the detailed assessment page. That page collects deeper experience answers and English screening before the final hiring review.
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div ref={ref} className="min-h-screen bg-canvas">
      <section data-theme-surface="dark" data-testid="careers-hero" className={`${CAREERS_HERO_SECTION_CLASS} site-image-hero`}>
        <HeroBackground imageUrl={heroImageUrl} imageAlt={cmsHero.imageAlt || 'Organized office desk overlooking a trucking yard'} />
        <div className="absolute inset-0 grain-overlay" />

        <div data-hero-text-parallax="true" className={PAGE_HERO_CONTAINER_CLASS}>
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.44fr)] gap-10 lg:gap-12 items-center">
            <div className="max-w-4xl">
              <p className={PAGE_HERO_EYEBROW_CLASS}>{hero.eyebrow}</p>
              <h1 aria-label={heroTitle} className={PAGE_HERO_H1_CLASS}>
                {renderGoldHeroTitle(heroTitle)}
              </h1>
              <div className={PAGE_HERO_BODY_CLASS}>
                {heroIntroParagraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              </div>
              <SiteHeroCtas
                content={hero}
                className={PAGE_HERO_CTA_ROW_CLASS}
                primaryClassName={HERO_CTA_PRIMARY_CLASS}
                secondaryClassName={HERO_CTA_SECONDARY_CLASS}
                tertiaryClassName={HERO_CTA_SECONDARY_CLASS}
              />
            </div>

            <div className="reveal reveal-delay-2 hidden lg:block">
              <div className="ml-auto max-w-sm border-l border-gold/25 pl-8 space-y-7">
                {HERO_SIGNALS.map(({ label, icon: Icon }) => (
                  <div key={label} className="flex items-center gap-5">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/45 bg-canvas/45">
                      <Icon className="h-5 w-5 text-accent-ink" strokeWidth={1.55} />
                    </span>
                    <p className="font-body text-[11px] uppercase tracking-[0.24em] text-accent-ink/85">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <HeroScrollRail />
      </section>

      <div className="topic-rail overflow-hidden border-y border-ink/10 bg-surface">
        <div className="marquee-container">
          <div className="marquee-track">
            {[...CAREERS_MARQUEE_ITEMS, ...CAREERS_MARQUEE_ITEMS].map((item, index) => (
              <span key={`${item}-${index}`} className="inline-flex items-center gap-3 px-9 font-body text-[9px] uppercase tracking-[0.44em] text-copy/24 whitespace-nowrap">
                {item}
                <span className="h-1 w-1 rotate-45 bg-gold/45" />
              </span>
            ))}
          </div>
        </div>
      </div>

      <CareersIntroBand />

      <div className="careers-story">
        <section id="careers-standard" data-testid="careers-standard" className="careers-applicant-note">
          <div className="careers-shell">
            <span className="careers-small-label">For individual applicants</span>
            <p><span>Looking for documentation for your trucking company? Explore our <a href="/paperwork">paperwork packages</a>.</span></p>
          </div>
        </section>

        <section id="roles" data-testid="careers-roles" className="careers-chapter">
          <div className="careers-shell">
            <p className="careers-section-label">01 / {intro.eyebrow || 'Candidate paths'}</p>
            <div className="careers-heading-row"><h2>{introTitle}</h2><p>{introBody}</p></div>
            <PageStoryImage testId="careers-workspace-image" src={CAREERS_IMAGES.truck} alt="A truck parked at a freight facility at night" position="center 58%" />
            <div className="careers-role-list">
              {ROLE_AREAS.slice(0, 2).map(({title,copy})=><article key={title}>
                <div className="careers-role-name"><h3>{title.split(' — ')[0]}</h3><span className="careers-role-availability">Open now</span></div>
                <p>{copy}</p>
                <FlowButton as="a" href="#application-form" className="careers-role-action">Apply for {title.split(' — ')[0]}</FlowButton>
              </article>)}
            </div>
            <div className="careers-upcoming"><span className="careers-small-label">At full capacity</span><p>Accounting (At full capacity) <span aria-hidden="true">/</span> HR (At full capacity) <span aria-hidden="true">/</span> Track &amp; trace (At full capacity)</p><span>Applications are currently closed for these departments.</span></div>
          </div>
        </section>

        <section data-testid="careers-expectations" className="careers-chapter careers-chapter-tint">
          <div className="careers-shell">
            <p className="careers-section-label">02 / Why work with us</p>
            <div className="careers-heading-row"><h2>Learn the work.<br /><em>Prepare for a carrier role.</em></h2><p>Assigned learning helps you understand the role before a carrier makes a hiring decision.</p></div>
            <div className="careers-work-skills">{ROLE_WORK_MODES.map(({title,copy,icon:Icon})=><article key={title}><Icon size={24} strokeWidth={1.3} aria-hidden="true" /><h3>{title}</h3><p>{copy}</p></article>)}</div>
            <p className="careers-skills-note">We review your progress before inviting you to the next stage.</p>
            <div className="careers-fit-heading"><h3>Is Safety or ELD support a good fit?</h3><p>Both roles involve records and direct contact with drivers. Consider whether these daily tasks suit you.</p></div>
            <div className="careers-fit-grid"><div><span className="careers-small-label">You may enjoy this work if</span><ul>{GOOD_FIT_ITEMS.map(item=><li key={item}><CheckCircle2 size={16} aria-hidden="true" /><span>{item}</span></li>)}</ul></div><div><span className="careers-small-label">This may not fit right now if</span><ul>{NOT_GOOD_FIT_ITEMS.map(item=><li key={item}><XCircle size={16} aria-hidden="true" /><span>{item}</span></li>)}</ul></div></div>
          </div>
        </section>

        <section data-testid="careers-process" className="careers-chapter">
          <div className="careers-shell">
            <p className="careers-section-label">03 / The selection process</p>
            <div className="careers-heading-row"><h2>From application<br />to carrier referral.</h2><p>We review your application and questionnaire before assigning training.</p></div>
            <div className="careers-selection-layout">
              <div className="careers-selection-context">
                <div className="careers-selection-image"><img src={CAREERS_IMAGES.desk} alt="" aria-hidden="true" loading="lazy" /><span className="careers-small-label">Preparing for work with US carriers</span></div>
                <div className="careers-training-note"><span className="careers-small-label">Selection training</span><h3>Free for candidates.</h3><p>Assigned training is free; public courses are not required. We explain completion and test requirements before modules are assigned.</p></div>
                <div className="careers-hiring-handoff"><span>You apply</span><ArrowRight size={14} aria-hidden="true" /><span>Magellan may refer you</span><ArrowRight size={14} aria-hidden="true" /><span>Carrier decides on hiring</span></div>
              </div>
              <ol className="careers-selection-steps">{HIRING_PROCESS.map(([title,copy],index)=><li key={title}><span className="careers-step-number">0{index+1}</span><div><h3>{title}</h3><p>{copy}</p></div></li>)}</ol>
            </div>
            <p className="careers-process-notice">A referral does not guarantee employment.</p>
          </div>
        </section>

        <section data-testid="careers-before-apply" className="careers-chapter careers-chapter-tint">
          <div className="careers-shell">
            <p className="careers-section-label">04 / Before you apply</p>
            <div className="careers-heading-row"><h2>What to share<br />in your application.</h2><p>A few specific examples help us understand your starting point.</p></div>
            <PageStoryImage testId="careers-evidence-image" src={CAREERS_IMAGES.review} alt="Reviewing paperwork with a pen beside a laptop" position="center 63%" />
            <div className="careers-evidence-grid">{BEFORE_APPLY_ITEMS.map((item,index)=><article key={item}><span className="careers-small-label">{["Safety experience", "ELD experience", "Your availability", "New to logistics"][index]}</span><p>{item}</p></article>)}</div>
            <div className="careers-application-invitation"><div><h3>Apply for Safety or ELD.</h3></div><FlowButton as="a" href="#application-form" className="careers-invitation-action">Start application</FlowButton></div>
          </div>
        </section>

        <section data-testid="careers-faq" className="careers-chapter careers-questions">
          <div className="careers-shell">
            <p className="careers-section-label">Before you start</p>
            <h2>Training and hiring questions.</h2>
            <Accordion type="single" collapsible className="careers-faq-list" data-testid="careers-faq-accordion">
              {CAREERS_FAQS.map(([question,answer],index)=><AccordionItem key={question} value={`careers-faq-${index}`} data-testid={`careers-faq-item-${index}`} className="careers-faq-item"><AccordionTrigger className="careers-faq-trigger"><span className="careers-faq-number">0{index+1}</span><span>{question}</span></AccordionTrigger><AccordionContent className="careers-faq-answer">{answer}</AccordionContent></AccordionItem>)}
            </Accordion>
          </div>
        </section>
      </div>

      <section id="application-form" data-testid="careers-form-section" className="candidate-application-section bg-canvas">
        <div className="site-content-shell">
          <div className="reveal candidate-application-heading">
            <p>Candidate application</p>
            <h2>Your application</h2>
            <p>
              Tell us about your background, skills and availability, and the Safety or ELD work that interests you. A CV is optional; if you include one, prepare one PDF no larger than 5 MB for the final step.
            </p>
          </div>

          <div className="partner-wizard candidate-wizard">
            <aside className="partner-wizard-progress" aria-label="Application progress">
              <p className="partner-wizard-eyebrow">Application progress</p>
              <div className="partner-wizard-track" role="progressbar" aria-label="Application steps" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progressPercent}>
                <span style={{ width: `${progressPercent}%` }} />
              </div>
              <p className="partner-wizard-percent">{progressPercent}%</p>
              <nav aria-label="Candidate application steps">
                {APPLICATION_STEPS.map((step, index) => (
                  <button
                    key={step.key}
                    type="button"
                    onClick={() => goToUnlockedStep(index)}
                    disabled={index > maxUnlockedStep}
                    aria-disabled={index > maxUnlockedStep}
                    aria-current={index === currentStep ? 'step' : undefined}
                  >
                    <span className="partner-wizard-eyebrow">{step.eyebrow}</span>
                    <span>{step.title}</span>
                  </button>
                ))}
              </nav>
            </aside>

            <div>
              {submitted ? (
                <div data-testid="careers-form-success" className="partner-wizard-panel partner-wizard-success">
                  <CheckCircle2 size={42} aria-hidden="true" />
                  <h3>Base Application Received</h3>
                  <p>Redirecting you into the detailed assessment page now.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="partner-wizard-panel candidate-wizard-panel" data-testid="careers-application-form" aria-labelledby="candidate-step-heading">
                  <div className="partner-wizard-heading">
                    <p className="partner-wizard-eyebrow">{activeStep.eyebrow} / 0{APPLICATION_STEPS.length}</p>
                    <h3 id="candidate-step-heading">{activeStep.title}</h3>
                    <p>{activeStep.helper}</p>
                  </div>

                  {renderStepFields()}

                  {missingRequired.length ? (
                    <p className="partner-wizard-note">Required before continuing: {missingRequired.join(', ')}</p>
                  ) : null}

                  <div className="partner-wizard-actions">
                    <FlowButton
                      type="button"
                      onClick={goBack}
                      disabled={currentStep === 0}
                      showArrow={false}
                    >
                      <ChevronLeft size={16} aria-hidden="true" /> Back
                    </FlowButton>
                    <FlowButton
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="careers-form-submit"
                    >
                      {isSubmitting ? (
                        <>Processing <Loader2 className="w-4 h-4 animate-spin" /></>
                      ) : currentStep === APPLICATION_STEPS.length - 1 ? (
                        <>Submit and Continue to Assessment </>
                      ) : (
                        <>Continue </>
                      )}
                    </FlowButton>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
